import React, { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { RateProvider } from "@/context/RateContext";
import Login from "@/pages/Login";
import Home from "@/pages/Home";
import Projects from "@/pages/Projects";
import Studio from "@/pages/Studio";
import Share from "@/pages/Share";
import MediaLibrary from "@/pages/MediaLibrary";
import Settings from "@/pages/Settings";
import { AppBackdrop } from "@/components/AppBackdrop";

const Guard = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen relative z-[1] flex items-center justify-center text-zinc-500 font-mono text-sm">loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

let booted = false;
// Remember where a signed-in user was; if the page reloads onto the public home, bring them back.
const RouteMemory = () => {
  const loc = useLocation();
  const armed = useRef(!booted);
  const first = armed.current;
  useEffect(() => {
    booted = true;
    armed.current = false;
    if (/^\/(studio|projects|settings)/.test(loc.pathname)) sessionStorage.setItem("ax_last_route", loc.pathname + loc.search);
  }, [loc]);
  const last = sessionStorage.getItem("ax_last_route");
  if (first && loc.pathname === "/" && last && localStorage.getItem("ax_token")) return <Navigate to={last} replace />;
  return null;
};

const LegacyRedirect = () => {
  const { projectId, category } = useParams();
  const map = { image: "image", video: "video", audio: "audio", remix: "remix", lipsync: "video", face_swap: "remix" };
  return <Navigate to={`/studio/${projectId}/${map[category] || "image"}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <RateProvider>
        <BrowserRouter>
          <AppBackdrop />
          <RouteMemory />
          <Toaster position="bottom-right" theme="dark" richColors offset="84px" />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/share/:token" element={<Share />} />
            <Route path="/projects" element={<Guard><Projects /></Guard>} />
            <Route path="/settings" element={<Guard><Settings /></Guard>} />
            <Route path="/library" element={<Guard><MediaLibrary /></Guard>} />
            <Route path="/studio/:projectId/:section" element={<Guard><Studio /></Guard>} />
            <Route path="/home/:projectId" element={<Guard><LegacyRedirect /></Guard>} />
            <Route path="/workspace/:projectId/:category" element={<Guard><LegacyRedirect /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </RateProvider>
    </AuthProvider>
  );
}

export default App;
